import {Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import {classToClass, plainToClass} from "class-transformer";
import {ConfirmationService, SelectItem} from 'primeng/api';
import {GrowlService} from "@app/core";
import {DefaultRowsDT, RowsPerPageDT} from "@app/shared/classes/common";
import {Contact, ExternalContactType} from '@app/shared/classes/contact';
import {RegExpEmail} from '@app/shared/classes/reg-exp';
import {OrgAddress} from "@app/shared/classes/org-address";
import {OrgContact} from "@app/shared/classes/org-contact";
import {EnumType} from '@app/shared/enums/enum-type.enum';
import {Format} from '@app/shared/enums/format.enum';
import {ApprovalStatusType} from '@app/shared/enums/approval-status-type.enum';
import {Validation} from '@app/shared/enums/validation.enum';
import {AddressType} from '@app/shared/enums/address-type.enum';

import {ExternalContactSubtype, OrgType, PartnerAddress, PartnerContact, TradingPartner} from '../../classes/trading-partner';
import {TradingPartnerService} from '@app/shared/services/trading-partner.service';
import {CommonDataService} from '@app/shared/services/common-data.service';
import {CommonTransformationService} from '@app/shared/services/common-transformation.service';
import {RestrictedSolutionManagerService} from '@app/shared/services/restricted-solution-manager.service';
import {PhoneService} from "@app/shared/services/phone.service";
import {ActionStatusTypeEnum, PrimeNgConfig, PrimeNgSupportService, TableColumn, TitleCasePipe, TradingPartnerType} from "@app/shared";

@Component({
  selector: 'app-org-contact',
  templateUrl: './org-contact.component.html',
  styleUrls: ['./org-contact.component.css'],
  providers: [ConfirmationService, RestrictedSolutionManagerService]
})
export class OrgContactComponent implements OnInit, OnChanges {

  ApprovalStatusType: typeof ApprovalStatusType = ApprovalStatusType;
  ExternalContactType: typeof ExternalContactType = ExternalContactType;
  Format: typeof Format = Format;
  OrgType: typeof OrgType = OrgType;
  PrimeNgConfig = PrimeNgConfig;
  RegExpEmail = RegExpEmail;
  Validation: typeof Validation = Validation;
  rowsPerPageDT = RowsPerPageDT;
  defaultRowsDT = DefaultRowsDT;

  disabledBillingContact: boolean = false;
  displayDialog: boolean = false;               //dialog to add/edit contact
  showDatable: boolean = true;                  //loads/refreshes datatable
  showAddContactBtn: boolean = false;
  errMsgForRepeatedMainContact: string;        // displays error message when main contact is repeated

  contactToEdit: PartnerContact | Contact;      //object modeled in contact dialog
  selectedContact: PartnerContact | Contact;    //selected contact to edit in dialog

  contactTypeDropDown: SelectItem[] = [];       //list of contact types for dropdown like main,billing,etc
  addrNameDropDown: SelectItem<PartnerAddress | OrgAddress>[] = [];          //list of address names from genInfo tab
  selectedAddress: PartnerAddress | OrgAddress; //object modeled for address dropdown
  contactColumns: TableColumn[];                        // column headers and fields to display on the address Table

  @Input() mainAddress: PartnerAddress | OrgAddress;
  @Input() dbaAddress: PartnerAddress | OrgAddress;
  @Input() otherAddresses: Array<PartnerAddress | OrgAddress>;
  @Input() tradingPartnerId: number;            //might vary based on entity exp: orgId, contractId, tradingPartnerId
  @Input() orgType: OrgType | TradingPartnerType;
  @Input() contacts: Array<PartnerContact | Contact>;
  @Input() tradingPartner: TradingPartner;

  @Output() handlerSave: EventEmitter<any> = new EventEmitter();

  constructor(private confirmationService: ConfirmationService, private commonTransformationService: CommonTransformationService,
              private restrictedSolMgrDataService: RestrictedSolutionManagerService, private tpService: TradingPartnerService,
              private titleCasePipe: TitleCasePipe, private commonDataService: CommonDataService, private growl: GrowlService,
              private phoneService: PhoneService, private primeNgService: PrimeNgSupportService) {
  }

  ngOnInit() {
    this.getContactTypes();
    this.loadAddresses();
    this.contactColumns = [
      {field: 'contactType', header: 'Contact Type'},
      {field: 'firstName', header: 'Name'},
      {field: 'title', header: 'Title'},
      {field: 'addrName', header: 'Address Name/Type'},
      {field: 'officePhone', header: 'Work Phone'},
      {field: 'faxPhone', header: 'Fax Phone'},
      {field: 'mobilePhone', header: 'Mobile Phone'},
      {field: 'email', header: 'Email'},
      {field: 'action', header: 'Actions'}
    ];
  }

  ngOnChanges() {
    if (this.otherAddresses) {
      this.loadAddresses();
    }
  }

  /**
   * @desc get a list of contactTypes for dropdown. Org and Trading partner has list different lists of enum
   */
  getContactTypes(): void {
    if (this.orgType == OrgType.Org) {
      this.commonDataService.getEnumPairAsDropdown(EnumType.OrgContact).subscribe(res => {
        this.contactTypeDropDown = res;
      });
    } else {
      this.commonDataService.getEnumPairAsDropdown(EnumType.ExternalContact).subscribe(res => {
        this.contactTypeDropDown = res;
      });
    }
  }

  onChangeContactType(contactType: ExternalContactType): void {
    const contactIndex = this.contacts.findIndex(contact => contact.contactType == ExternalContactType.Billing);
    this.validateUniqueContact();
    if (contactType == ExternalContactType.Main && this.orgType == OrgType.Org) {
      this.restrictedSolMgrDataService.checkOrgMainContactUniqueness().subscribe(res => {
        this.showAddContactBtn = <boolean>res.IfUniqueMainContact;
        this.errMsgForRepeatedMainContact = this.showAddContactBtn ? '' : 'Main contact type already exists';
      });
    } else if (contactType == ExternalContactType.Main && this.orgType != OrgType.Org) {
      this.tpService.checkPartnerMainContactUniqueness(this.tradingPartnerId).subscribe(res => {
        this.showAddContactBtn = <boolean>res.IfUniqueMainContact;
        this.errMsgForRepeatedMainContact = this.showAddContactBtn ? '' : 'Main contact type already exists';
      });
    } else if (contactType == ExternalContactType.Billing && contactIndex > -1 && this.contactToEdit.id != this.contacts[contactIndex].id) {
      this.showAddContactBtn = false;
      this.errMsgForRepeatedMainContact = this.showAddContactBtn ? '' : 'Billing contact type already exists';
    } else {
      this.errMsgForRepeatedMainContact = null;
      this.showAddContactBtn = true;
    }
  }

  /**
   * @desc gets addresses from @Input push each lines address name into the this.addrNameDropDown. if dbaAddress.addrName is same as mainAddress.addrName, then dont push into addrNameDropDown
   * @returns this.addrNameDropDown is populated with address names
   */
  loadAddresses(): void {
    this.addrNameDropDown = [];
    for (let addr of this.otherAddresses) {
      const label = addr.addrName + " / " + this.titleCasePipe.transform(addr.addrType);
      this.addrNameDropDown.push({label: label, value: addr});
    }

    const mainAddressInTheDropdown = this.otherAddresses?.find(addr => addr.addrType === AddressType.Main);
    if (!mainAddressInTheDropdown) {
      const label = this.mainAddress.addrName + " / " + this.titleCasePipe.transform(this.mainAddress.addrType);
      this.addrNameDropDown.push({label: label, value: this.mainAddress});
    }

    const dbaAddressInTheDropdown = this.otherAddresses?.find(addr => addr.addrType === AddressType.Dba);
    if (this.dbaAddress.id && (this.mainAddress.addrName != this.dbaAddress.addrName) && !dbaAddressInTheDropdown) {
      const label = this.dbaAddress.addrName + " / " + this.titleCasePipe.transform(this.dbaAddress.addrType);
      this.addrNameDropDown.push({label: label, value: this.dbaAddress});
    }
  }

  /**
   * @desc opens up dialog to add new contact
   * @returns this.displayDialog is set to true to open dialog
   */
  showAddContact(): void {
    if (this.orgType == OrgType.Org) {
      this.contactToEdit = new OrgContact();
      //TODO: Verify if this has to be of instance OrgContact
    } else {
      this.contactToEdit = new PartnerContact();
    }
    this.selectedContact = null;
    this.selectedAddress = null;
    this.displayDialog = true;
    this.disableBillingContact();
  }

  /**
   * @desc metadata is set to form payload along with dialog info. new line item is pushed to table
   * @returns this.displayDialog is set to false to close dialog
   */
  addContact(contactToEdit: PartnerContact | Contact | OrgContact, selectedAddress: PartnerAddress | OrgAddress): void {
    if (contactToEdit instanceof PartnerContact) {
      contactToEdit.partnerId = this.tradingPartnerId;          // we should not need this to save
      contactToEdit.subtype = ExternalContactSubtype.Partner;
      contactToEdit.addressId = selectedAddress ? selectedAddress.id : null;
      contactToEdit.addrName = selectedAddress ? selectedAddress.addrName : null;
    }
    if (contactToEdit instanceof OrgContact) {
      contactToEdit.addressId = selectedAddress ? selectedAddress.id : null;
      contactToEdit.addrName = selectedAddress ? selectedAddress.addrName : null;
    }

    contactToEdit.fullName = contactToEdit.firstName + '  ' + contactToEdit.lastName;
    this.contacts.push(contactToEdit);
    if (this.tradingPartner.billingContactSameAsMain && contactToEdit.contactType === ExternalContactType.Main) {
      this.addOrUpdateBillingContact(contactToEdit);
    }
    this.displayDialog = false;
    this.handlerSave.emit(ActionStatusTypeEnum.ContactAdded);
  }

  /**
   * @desc opens up dialog to edit existing contact. selected row is copied into this.contactToEdit which is modeled in dialog
   * @returns this.displayDialog is set to true to open dialog
   */
  showEditContact(selectedContact: PartnerContact | OrgContact): void {
    this.selectedContact = selectedContact;
    this.contactToEdit = classToClass(selectedContact);

    const index: number = this.addrNameDropDown.findIndex(address => address.value.id == selectedContact.addressId);
    if (index > -1) {
      this.selectedAddress = this.addrNameDropDown[index].value;
    }

    this.displayDialog = true;
    this.disableBillingContact();
  }

  /**
   * @desc metadata is set to form payload along with dialog info. existing line item is replaced in table
   * @returns this.displayDialog is set to false to close dialog
   */
  updateContact(contactToEdit: PartnerContact | OrgContact, selectedAddress: PartnerAddress | OrgAddress): void {
    const index = this.contacts.findIndex(i => i == this.selectedContact);

    if (index >= 0) {
      contactToEdit.addressId = selectedAddress ? selectedAddress.id : null;
      contactToEdit.addrName = selectedAddress ? selectedAddress.addrName : null;

      contactToEdit.fullName = contactToEdit.firstName + '' + contactToEdit.lastName;

      this.contacts[index] = contactToEdit;
      if (this.tradingPartner.billingContactSameAsMain && contactToEdit.contactType === ExternalContactType.Main) {
        this.addOrUpdateBillingContact(contactToEdit);
      }
      this.displayDialog = false;
      this.handlerSave.emit(ActionStatusTypeEnum.ContactUpdated);
    }
  }

  /**
   * @desc finds the index of selected line item to delete and delete/splice line form table and persists changes in db
   *@returns this.contacts array is updated
   */
  deleteContact(selectedContact: PartnerContact | Contact): void {
    const index = this.contacts.findIndex(i => i === selectedContact);
    if (index >= 0) {
      this.confirmationService.confirm({
        message: `Are you sure that you want to delete ${selectedContact.contactType} contact ?`,
        header: 'Delete Contact Confirmation',
        accept: () => {
          this.contacts.splice(index, 1);
          if (selectedContact.contactType === ExternalContactType.Billing) {
            this.tradingPartner.billingContactSameAsMain = false;
          }
          this.handlerSave.emit(ActionStatusTypeEnum.ContactDeleted);
        },
        reject: () => {
        }
      });
    } else {
      console.error("could not find the contact to delete");
    }
  }

  /**
   * @desc close contact dialog
   */
  closeDialog(): void {
    this.errMsgForRepeatedMainContact = null;
    this.displayDialog = false;
  }

  /**
   * @desc add billing address if it is same as main address
   */
  addBillingContactIfSameAsMain(billingContactSameAsMain: boolean): void {
    if (billingContactSameAsMain) {
      const mainContactIndex = this.contacts.findIndex(row => row.contactType === ExternalContactType.Main);
      const billingContactIndex = this.contacts.findIndex(row => row.contactType === ExternalContactType.Billing);
      if (billingContactIndex > -1 && mainContactIndex > -1) {
        this.confirmationService.confirm({
          message: `Are you sure you want to override the existing billing contact ?`,
          header: 'Update Billing Contact',
          accept: () => {
            this.addOrUpdateBillingContact(this.contacts[mainContactIndex]);
            this.handlerSave.emit(ActionStatusTypeEnum.Update);
          },
          reject: () => {
          }
        });
      } else {
        if (mainContactIndex > -1) {
          this.confirmationService.confirm({
            message: `Are you sure you want to add billing contact same as main contact?`,
            header: 'Add Billing Contact',
            accept: () => {
              let billingContact = classToClass(this.contacts[mainContactIndex], {excludePrefixes: ['id', 'version']});
              billingContact.contactType = ExternalContactType.Billing;
              this.contacts.push(billingContact);
              this.handlerSave.emit(ActionStatusTypeEnum.ContactAdded);
            },
            reject: () => {
            }
          });
        }
      }
    } else {
      this.handlerSave.emit(ActionStatusTypeEnum.ContactAdded);
    }
  }

  addOrUpdateBillingContact(mainAddress: Contact): void {
    const index = this.contacts.findIndex(row => row.contactType === ExternalContactType.Billing);
    if (index == -1) {
      const address: Contact = plainToClass(Contact, mainAddress);
      address.contactType = ExternalContactType.Billing;
      this.contacts.push(classToClass(address, {excludePrefixes: ['id', 'version']}));
    } else {
      const contactReplaced = this.contacts[index];
      this.contacts[index] = classToClass(mainAddress, {excludePrefixes: ['id', 'version']});
      this.contacts[index].id = contactReplaced.id;
      this.contacts[index].version = contactReplaced.version;
      this.contacts[index].contactType = contactReplaced.contactType;
    }
  }

  disableBillingContact(): void {
    if (this.tradingPartner.billingContactSameAsMain && this.contactToEdit && this.contactToEdit.id && this.contactToEdit.contactType == ExternalContactType.Billing) {
      this.disabledBillingContact = true;
    } else {
      this.disabledBillingContact = false;
    }
  }

  validateUniqueContact(): void {
    this.showAddContactBtn = true;
    this.errMsgForRepeatedMainContact = null;
    let selectedContactTypes = this.contacts.filter(contact => contact.contactType == this.contactToEdit.contactType);
    let contactPresent = false;
    selectedContactTypes.forEach(contact => {
      if (this.contactToEdit.firstName == contact.firstName && this.contactToEdit.lastName == contact.lastName && this.selectedAddress && contact.addressId == this.selectedAddress.id) {
        contactPresent = true;
      }
    });
    if (contactPresent) {
      this.showAddContactBtn = false;
      this.errMsgForRepeatedMainContact = 'Contact type already exists';
    }
  }
}




